import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { InputBox } from "../../Common";
import styles from "./addbank.module.css";
import {
  addBankAccount,
  bankList,
  getBankDetails,
  getAccountNumber,
  deleteAccount,
} from "../../api";
import { validateForm } from "../../utils/validation";
import Button from "../../Common/Button";
import DataTable from "../../Common/DataTable/DataTable";
import singleBackArrow from "../../public/images/singleBackArrow.svg";
import CustomSpinner from "../../Common/Spinner";
import { bankColumns } from "../../utils/tableHeader";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { DropDown } from "../../Common";

const AddBank = ({
  custId,
  setShowForm,
  showForm,
  userData,
  featureAccess,
}) => {
  const [formData, setFormData] = useState({
    isMyAccount: true,
    beneficiaryName: userData?.fullName || "",
    beneficiaryAccountNumber: "",
    beneficiaryAccountType: "SAVING_ACCOUNT",
    beneficiarySwiftABA: "",
    bankName: "",
    nationalBankId: "",
    bankAddress: "",
    bankIFSC: "",
    bankCountry: userData?.country || "",
    isIndian: false,
  });
  const [bankDetails, setBankDetails] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectedBank, setSelectedBank] = useState("");
  const [selectedAccountType, setSelectedAccountType] =
    useState("SAVING_ACCOUNT");
  const [banks, setBanks] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [decryptedAccounts, setDecryptedAccounts] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState({});
  const accountTypes = [
    { label: "Saving Account", value: "SAVING_ACCOUNT" },
    { label: "Current Account", value: "CURRENT_ACCOUNT" },
  ];
  const [deleteLoading, setDeleteLoading] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);
  const [showModalSuccess, setShowModalSuccess] = useState(false);

  const fetchBankList = async () => {
    try {
      setDataLoading(true);
      const response = await bankList();
      const bankDetailsRes = await getBankDetails(custId);

      if (bankDetailsRes.status === 200) {
        setBankDetails(bankDetailsRes?.data?.data);
      }
      if (response.status === 200) {
        setBanks(response.data.data);
      }
    } catch (error) {
    }
      setDataLoading(false);
  };
  useEffect(() => {
    fetchBankList();
  }, [successMessage]);

  const handleToggleAccountVisibility = async (bankItem) => {
    const bankId = bankItem.stoUserBankId;

    if (decryptedAccounts[bankId]) {
      setDecryptedAccounts((prev) => {
        const updated = { ...prev };
        delete updated[bankId];
        return updated;
      });
      return;
    }
    setLoadingAccounts((prev) => ({ ...prev, [bankId]: true }));

    try {
      const response = await getAccountNumber(bankId);
      if (response.code === 200 && response.data?.accountNumber) {
        setDecryptedAccounts((prev) => ({
          ...prev,
          [bankId]: response.data.accountNumber,
        }));
      } else {
        setErrorMessage("Failed to fetch account number:", response);
      }
    } catch (error) {
    } finally {
      setLoadingAccounts((prev) => ({ ...prev, [bankId]: false }));
    }
  };
  const handleBankAccountDelete = (bankItem) => {
    setBankToDelete(bankItem);
    setShowDeleteModal(true);
    setShowModalSuccess(false);
  };
  const handleConfirmDelete = async () => {
    if (!bankToDelete) return;

    const id = bankToDelete.id;
    const stoUserBankId = bankToDelete.stoUserBankId;
    setDeleteLoading((prev) => ({ ...prev, [id]: true }));

    try {
      const payload = {
        stoUserBankId: stoUserBankId,
        isDeleted: true,
        custId: custId,
      };
      const response = await deleteAccount(id, payload);

      if (response.code === 200) {
        await fetchBankList();

        setDecryptedAccounts((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        setShowModalSuccess(true);
        setTimeout(() => {
          setShowDeleteModal(false);
          setBankToDelete(null);
          setShowModalSuccess(false);
        }, 3000);
      } else {
        setErrorMessage("Failed to delete bank account. Please try again.");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (error) {
      setErrorMessage("Something went wrong");
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
    if (errorMessage) {
      setErrorMessage("");
    }
  };
  const requiredFields = [
    "beneficiaryName",
    "beneficiaryAccountNumber",
    "beneficiaryAccountType",
    "beneficiarySwiftABA",
    "bankName",
    "bankAddress",
    "bankCountry",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDataLoading(true);

    const selectedBanks = banks.find(
      (bank) => bank.code.replace(/_/g, " ") === selectedBank
    );

    const isIndian = selectedBanks?.isIndian;
    if (isIndian && !requiredFields.includes("bankIFSC")) {
      requiredFields.push("bankIFSC");
    }

    if (validateForm(requiredFields, setErrors, formData)) {
      const finalBankName =
        selectedBank === "OTHER INDIAN" || selectedBank === "OTHER NONINDIAN"
          ? formData.bankName
          : banks.find((bank) => bank.code.replace(/_/g, " ") === selectedBank)
              ?.name || formData.bankName;
      try {
        const response = await addBankAccount({
          ...formData,
          custId: custId,
          bankName: finalBankName,
        });
        if (response.status === 200) {
          setFormData({
            isMyAccount: true,
            beneficiaryName: userData?.fullName || "",
            beneficiaryAccountNumber: "",
            beneficiaryAccountType: "SAVING_ACCOUNT",
            beneficiarySwiftABA: "",
            bankName: "",
            nationalBankId: "",
            bankAddress: "",
            bankIFSC: "",
            bankCountry: userData?.country || "",
          });
          setSelectedBank("");
          setSelectedAccountType("Saving Account");
          setErrors({});
          setErrorMessage("");
          setSuccessMessage("Bank Added successfully!");
          setTimeout(() => {
            setSuccessMessage("");
            handleShowForm(showForm);
          }, 3000);
        } else {
          setErrorMessage(
            response.data.message || "Failed to Add bank. Please try again."
          );
        }
      } catch (error) {}
    }
    setDataLoading(false);
  };

  const handleShowForm = (data) => {
    setShowForm(!data);
  };
  const columns = bankColumns(
    decryptedAccounts,
    loadingAccounts,
    handleToggleAccountVisibility,
    featureAccess
  );
  const handleAccountTypeChange = (
    val,
    formData,
    setFormData,
    setSelectedAccountType,
    errors,
    setErrors
  ) => {
    setSelectedAccountType(val);
    setFormData({
      ...formData,
      beneficiaryAccountType: val,
    });
    if (errors.beneficiaryAccountType) {
      setErrors((prev) => ({ ...prev, beneficiaryAccountType: "" }));
    }
  };

  return (
    <>
      <div className={styles.container} style={{ position: "relative" }}>
        {showForm ? (
          <>
            {userData?.accountStatus === "APPROVED" && (
              <div
                style={{
                  marginRight: "30px",
                  marginTop: "10px",
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "160px",
                }}
              >
                {featureAccess?.writeAccess && (
                  <Button
                    onClick={() => handleShowForm(showForm)}
                    title={"Add Bank"}
                  />
                )}
              </div>
            )}
            {dataLoading ? (
              <CustomSpinner />
            ) : (
              <>
                {Array.isArray(bankDetails) && bankDetails.length > 0 ? (
                  <DataTable
                    columns={columns}
                    list={bankDetails}
                    handleBankAccountDelete={handleBankAccountDelete}
                    successMessage={successMessage}
                    errorMessage={errorMessage}
                  />
                ) : (
                  <p
                    style={{
                      color: "white",
                      marginTop: "80px",
                      textAlign: "center",
                    }}
                  >
                    No Data Found
                  </p>
                )}
              </>
            )}
          </>
        ) : (
          <div className={styles.createRoleCard}>
            <div className={styles.card}>
              <form onSubmit={handleSubmit}>
                <h3 style={{ color: "white" }}>
                  <img
                    alt="Back"
                    src={singleBackArrow}
                    onClick={() => handleShowForm(showForm)}
                    className={styles.arrowButton}
                  />
                  Add Bank
                </h3>
                <Row style={{ margin: "20px 0px" }}>
                  <Col md={6} className={styles.inputDiv}>
                    <label>
                      Beneficiary Name{" "}
                      <InputBox
                        placeholder="Enter Beneficiary Name"
                        type="text"
                        onChange={handleChange}
                        name="beneficiaryName"
                        value={formData.beneficiaryName}
                        disabled
                      />
                      {errors.beneficiaryName && (
                        <span className={styles.error}>
                          {errors.beneficiaryName}
                        </span>
                      )}
                    </label>
                  </Col>
                  <Col md={6} className={styles.inputDiv}>
                    <label>
                      Bank Country
                      <InputBox
                        placeholder="Enter Bank Country"
                        type="text"
                        onChange={handleChange}
                        name="bankCountry"
                        value={formData.bankCountry}
                        disabled
                      />
                      {errors.bankCountry && (
                        <span className={styles.error}>
                          {errors.bankCountry}
                        </span>
                      )}
                    </label>
                  </Col>
                </Row>

                <Row style={{ margin: "20px 0px" }}>
                  <Col md={6} className={styles.inputDiv}>
                    <label>
                      Beneficiary Account Number{" "}
                      <span className={styles.required}>*</span>
                      <InputBox
                        placeholder="Enter Beneficiary Account Number"
                        type="text"
                        onChange={handleChange}
                        name="beneficiaryAccountNumber"
                        value={formData.beneficiaryAccountNumber}
                      />
                      {errors.beneficiaryAccountNumber && (
                        <span className={styles.error}>
                          {errors.beneficiaryAccountNumber}
                        </span>
                      )}
                    </label>
                  </Col>
                  <Col md={6} className={styles.inputDiv}>
                    <DropDown
                      label="Beneficiary Account Type"
                      name="beneficiaryAccountType"
                      data={accountTypes}
                      value={selectedAccountType}
                      onChange={(val) =>
                        handleAccountTypeChange(
                          val,
                          formData,
                          setFormData,
                          setSelectedAccountType,
                          errors,
                          setErrors
                        )
                      }
                      required
                      error={!!errors.beneficiaryAccountType}
                      helperText={errors.beneficiaryAccountType}
                    />
                  </Col>
                </Row>
                <Row style={{ margin: "20px 0px" }}>
                  <Col md={6} className={styles.inputDiv}>
                    <label>
                      Beneficiary SWIFT/ABA{" "}
                      <span className={styles.required}>*</span>
                      <InputBox
                        placeholder="Enter SWIFT/ABA Code"
                        type="text"
                        onChange={handleChange}
                        name="beneficiarySwiftABA"
                        value={formData.beneficiarySwiftABA}
                      />
                      {errors.beneficiarySwiftABA && (
                        <span className={styles.error}>
                          {errors.beneficiarySwiftABA}
                        </span>
                      )}
                    </label>
                  </Col>
                  <Col md={6} className={styles.inputDiv}>
                    <DropDown
                      label="Pick a Bank"
                      name="bankName"
                      data={banks.map((bank) => ({
                        label: bank.code.replace(/_/g, " "),
                        value: bank.code.replace(/_/g, " "),
                      }))}
                      value={selectedBank}
                      onChange={(val) => {
                        setSelectedBank(val);
                        const selectedBankObj = banks.find(
                          (bank) => bank.code.replace(/_/g, " ") === val
                        );
                        if (errors.bankName) {
                          setErrors((prev) => ({ ...prev, bankName: "" }));
                        }
                        if (
                          val === "OTHER INDIAN" ||
                          val === "OTHER NONINDIAN"
                        ) {
                          setFormData({
                            ...formData,
                            bankName: "",
                            nationalBankId: selectedBankObj?.code || "",
                            isIndian: val === "OTHER INDIAN",
                          });
                        } else {
                          setFormData({
                            ...formData,
                            bankName: selectedBankObj?.name || "",
                            nationalBankId: selectedBankObj?.code || "",
                            isIndian: selectedBankObj?.isIndian || false,
                          });
                        }
                      }}
                      required
                      error={
                        !!errors.bankName &&
                        (!selectedBank ||
                          selectedBank === "OTHER INDIAN" ||
                          selectedBank === "OTHER NONINDIAN")
                      }
                      helperText={
                        errors.bankName &&
                        (!selectedBank ||
                          selectedBank === "OTHER INDIAN" ||
                          selectedBank === "OTHER NONINDIAN")
                          ? errors.bankName
                          : ""
                      }
                    />
                  </Col>
                </Row>
                <Row style={{ margin: "20px 0px" }}>
                  <Col md={6} className={styles.inputDiv}>
                    <label>
                      Bank Address <span className={styles.required}>*</span>
                      <InputBox
                        placeholder="Enter Bank Address"
                        type="text"
                        onChange={handleChange}
                        name="bankAddress"
                        value={formData.bankAddress}
                      />
                      {errors.bankAddress && (
                        <span className={styles.error}>
                          {errors.bankAddress}
                        </span>
                      )}
                    </label>
                  </Col>
                  {formData.isIndian && (
                    <Col md={6} className={styles.inputDiv}>
                      <label>
                        IFSC Code <span className={styles.required}>*</span>
                        <InputBox
                          placeholder="Enter IFSC Code"
                          type="text"
                          onChange={handleChange}
                          name="bankIFSC"
                          value={formData.bankIFSC}
                        />
                        {errors.bankIFSC && (
                          <span className={styles.error}>
                            {errors.bankIFSC}
                          </span>
                        )}
                      </label>
                    </Col>
                  )}
                </Row>
                <Row style={{ margin: "20px 0px" }}>
                  {(selectedBank === "OTHER INDIAN" ||
                    selectedBank === "OTHER NONINDIAN") && (
                    <Col md={6} className={styles.inputDiv}>
                      <label>
                        Bank Name <span className={styles.required}>*</span>
                        <InputBox
                          placeholder="Enter Bank Name"
                          type="text"
                          onChange={handleChange}
                          name="bankName"
                          value={formData.bankName}
                        />
                        {errors.bankName &&
                          (selectedBank === "OTHER INDIAN" ||
                            selectedBank === "OTHER NONINDIAN") &&
                          !formData.bankName && (
                            <span className={styles.error}>
                              {errors.bankName}
                            </span>
                          )}
                      </label>
                    </Col>
                  )}
                </Row>

                {successMessage && (
                  <div style={{ margin: "20px 0px", textAlign: "center" }}>
                    <p style={{ color: "green" }}>{successMessage}</p>
                  </div>
                )}
                {errorMessage && (
                  <div style={{ margin: "20px 0px", textAlign: "center" }}>
                    <p style={{ color: "red" }}>{errorMessage}</p>
                  </div>
                )}

                <Row style={{ margin: "20px 0px" }}>
                  <Col md={12} className={styles.submitButton}>
                    <Button
                      onClick={handleSubmit}
                      title="Submit"
                      loading={dataLoading}
                      disabled={dataLoading}
                      width={"100%"}
                    />
                  </Col>
                </Row>
              </form>
            </div>
          </div>
        )}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onRequestClose={() => {
            setShowDeleteModal(false);
            setBankToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          message={`Are you sure you want to delete the bank account for ${bankToDelete?.beneficiaryName}? `}
          loading={deleteLoading[bankToDelete?.id] || false}
          showSuccess={showModalSuccess}
          successMessage="Account deleted successfully!"
          errorMessage={errorMessage}
        />
      </div>
    </>
  );
};

export default AddBank;
